import {createSelector} from "@reduxjs/toolkit";

import {selectPersistenceSingleParamError} from "./persistenceSlice";
import type {RevaRootState} from "./store";
import type {ApiQueryError} from "./types";

/**
 * The affected parameter carried in a single-parameter persistence error's
 * `errorArgs`, if any. `errorArgs` is loosely typed (`object | null`), so the
 * `param` field is accessed defensively.
 */
function errorArgsParam(error: ApiQueryError | null): unknown {
    return (error?.errorArgs as {param?: unknown} | null | undefined)?.param;
}

const errorForParamSelectorCache = new Map<string, (state: RevaRootState) => ApiQueryError | null>();

/**
 * Memoized selector (one per `param`) returning the single-parameter persistence
 * error only when it belongs to the given parameter, otherwise null.
 *
 * One `createSelector` instance is cached per `param` so the memoization survives
 * across renders/calls (mirrors `selectPermissionCheck` in authSelectors.ts).
 * @param param parameter matched against the error's `errorArgs.param`
 */
export const selectPersistenceErrorForSingleParam = (param: string) => {
    if (!errorForParamSelectorCache.has(param)) {
        errorForParamSelectorCache.set(
            param,
            createSelector([selectPersistenceSingleParamError], (error): ApiQueryError | null =>
                errorArgsParam(error) === param ? error : null,
            ),
        );
    }
    return errorForParamSelectorCache.get(param)!;
};

/**
 * Selector for the persistence error, excluding single-parameter errors (those
 * are surfaced per field via {@link selectPersistenceErrorForSingleParam}).
 */
export const selectPersistenceError = (state: RevaRootState): ApiQueryError | null => {
    const error = selectPersistenceSingleParamError(state);
    return errorArgsParam(error) ? null : error;
};

/**
 * True, wenn der Persistenzfehler an einem Parameter hängt und dessen Feld auf eine Entscheidung wartet
 * (Kommentardialog nach R500), siehe {@link selectPersistenceErrorForSingleParam}.
 */
export const selectHasPersistenceErrorForSomeParam = (state: RevaRootState): boolean =>
    errorArgsParam(selectPersistenceSingleParamError(state)) != null;
